import {
	FileTextIcon,
	FolderIcon,
	InboxIcon,
	MessageCircleIcon,
	PaperclipIcon,
	RefreshCwIcon,
	SearchIcon,
	SparklesIcon,
	UserIcon,
} from "lucide-react";

import { useMemo, useState } from "react";
import {
	Tool,
	ToolContent,
	ToolHeader,
	ToolInput,
	ToolOutput,
} from "@/components/ai-elements/tool";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { FAKE_FIRM, FAKE_MATTERS, FAKE_THREADS } from "./fixtures";
import type {
	FakeDraft,
	FakeMatter,
	FakeMessage,
	FakeTag,
	FakeThread,
	FakeUser,
} from "./types";

// Helpers

function formatDateShort(iso: string) {
	const d = new Date(iso);
	const now = new Date();
	const sameDay =
		d.getFullYear() === now.getFullYear() &&
		d.getMonth() === now.getMonth() &&
		d.getDate() === now.getDate();
	if (sameDay) {
		return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
	}
	const daysDelta = Math.floor(
		(now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24),
	);
	if (daysDelta < 7) {
		return d.toLocaleDateString([], { weekday: "short" });
	}
	return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function formatDateLong(iso: string) {
	return new Date(iso).toLocaleString([], {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

function getLastMessage(thread: FakeThread): FakeMessage {
	return thread.messages[thread.messages.length - 1];
}

function getFirstInbound(thread: FakeThread): FakeMessage | undefined {
	return thread.messages.find((m) => m.direction === "inbound");
}

function getDefaultDraftTarget(thread: FakeThread): string | null {
	// If there's a pre-generated fake draft, target the message it was written for.
	if (thread.fakeDraft) {
		const match = thread.messages.find(
			(m) => m.id === thread.fakeDraft?.targetMessageId,
		);
		if (match) return match.id;
	}
	// Otherwise default to the latest inbound message in the thread.
	for (let i = thread.messages.length - 1; i >= 0; i--) {
		if (thread.messages[i].direction === "inbound") {
			return thread.messages[i].id;
		}
	}
	return null;
}

// ReviewPage

export default function ReviewPage() {
	const users = FAKE_FIRM.users;
	const defaultUserId =
		users.find((u) => u.emailAttached)?.id ?? users[0]?.id ?? "";

	const [selectedUserId, setSelectedUserId] = useState<string>(defaultUserId);
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
	const [selectedMessageId, setSelectedMessageId] = useState<string | null>(
		null,
	);

	const selectedUser = users.find((u) => u.id === selectedUserId);

	const userThreads = useMemo(
		() => FAKE_THREADS.filter((t) => t.userId === selectedUserId),
		[selectedUserId],
	);

	const filteredThreads = useMemo(() => {
		let out = userThreads;
		if (searchQuery.trim()) {
			const q = searchQuery.toLowerCase();
			out = out.filter((t) => {
				const subj = t.subject.toLowerCase();
				const bodies = t.messages
					.map((m) => `${m.from.name} ${m.from.email} ${m.bodyText}`)
					.join(" ")
					.toLowerCase();
				return subj.includes(q) || bodies.includes(q);
			});
		}
		return [...out].sort((a, b) => {
			const ta = new Date(getLastMessage(a).receivedAt).getTime();
			const tb = new Date(getLastMessage(b).receivedAt).getTime();
			return tb - ta;
		});
	}, [userThreads, searchQuery]);

	const selectedThread =
		filteredThreads.find((t) => t.id === selectedThreadId) ??
		filteredThreads[0] ??
		null;

	const [previousThread, setPreviousThread] = useState<FakeThread | null>(null);
	if (selectedThread !== previousThread) {
		setPreviousThread(selectedThread);
		if (selectedThread) {
			setSelectedMessageId(getDefaultDraftTarget(selectedThread));
		}
	}

	const selectedMatter: FakeMatter | null = selectedThread?.matterMatch
		? (FAKE_MATTERS[selectedThread.matterMatch.matterId] ?? null)
		: null;

	return (
		<div className="flex h-full flex-col">
			<header className="flex items-center justify-between border-b py-3 pl-14 pr-6">
				<h1 className="text-base font-semibold">Review</h1>
				<UserSelector
					users={users}
					value={selectedUserId}
					onChange={(id) => {
						setSelectedUserId(id);
						setSelectedThreadId(null);
					}}
				/>
			</header>
			<div className="flex min-h-0 flex-1">
				{/* Left — thread list */}
				<div className="flex w-[380px] shrink-0 flex-col overflow-hidden">
					<div className="flex flex-col gap-2 border-b p-3">
						<div className="relative">
							<SearchIcon className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								placeholder="Search subject, sender, body…"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								className="pl-8 focus-visible:border-input focus-visible:ring-0"
							/>
						</div>
					</div>
					{selectedUser?.emailAttached === false ? (
						<div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
							<div>
								<InboxIcon className="mx-auto mb-2 size-8 opacity-40" />
								<p className="font-medium">No email attached</p>
								<p className="text-xs">
									{selectedUser.name} hasn't connected their inbox.
								</p>
							</div>
						</div>
					) : (
						<ScrollArea className="h-full min-h-0 flex-1">
							<div className="flex flex-col">
								{filteredThreads.length === 0 ? (
									<div className="p-6 text-center text-sm text-muted-foreground">
										No threads match.
									</div>
								) : (
									filteredThreads.map((thread) => (
										<ThreadListRow
											key={thread.id}
											thread={thread}
											selected={thread.id === selectedThread?.id}
											onSelect={() => setSelectedThreadId(thread.id)}
										/>
									))
								)}
							</div>
						</ScrollArea>
					)}
				</div>

				<Separator orientation="vertical" className="h-auto" />

				{/* Middle — thread detail (email-stack) */}
				<div className="flex min-w-0 flex-1 flex-col overflow-hidden">
					{selectedThread ? (
						<ThreadDetail
							thread={selectedThread}
							selectedMessageId={selectedMessageId}
							onSelectMessage={setSelectedMessageId}
						/>
					) : (
						<div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
							Select a thread to view.
						</div>
					)}
				</div>

				<Separator orientation="vertical" className="h-auto" />

				{/* Right — workspace (Draft | Matter) */}
				<div className="flex w-[480px] shrink-0 flex-col overflow-hidden">
					<WorkspacePane
						thread={selectedThread}
						matter={selectedMatter}
						selectedMessageId={selectedMessageId}
					/>
				</div>
			</div>
		</div>
	);
}

// Header user selector

function UserSelector({
	users,
	value,
	onChange,
}: {
	users: FakeUser[];
	value: string;
	onChange: (id: string) => void;
}) {
	return (
		<Select value={value} onValueChange={(v) => onChange(v ?? "")}>
			<SelectTrigger size="sm" className="min-w-48">
				<SelectValue placeholder="Select user" />
			</SelectTrigger>
			<SelectContent align="end" alignItemWithTrigger={false}>
				{users.map((u) => (
					<SelectItem key={u.id} value={u.id}>
						{u.name}
						{u.emailAttached ? "" : " — no email"}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

// Thread list

function ThreadListRow({
	thread,
	selected,
	onSelect,
}: {
	thread: FakeThread;
	selected: boolean;
	onSelect: () => void;
}) {
	const last = getLastMessage(thread);
	const first = getFirstInbound(thread) ?? thread.messages[0];
	const turns = thread.messages.length;
	const senderName = first.from.name;
	const preview = first.bodyText.slice(0, 120).replace(/\s+/g, " ");
	const intentTags =
		thread.tags?.filter((t) => t.axis === "intent").slice(0, 3) ?? [];
	const hasAttachments = thread.messages.some((m) => m.hasAttachments);

	return (
		<Button
			type="button"
			variant="ghost"
			onClick={onSelect}
			className={cn(
				"flex h-auto w-full flex-col items-stretch justify-start gap-1 whitespace-normal rounded-none border-b px-3 py-2.5 text-left font-normal",
				selected && "bg-muted",
			)}
		>
			<div className="flex items-center gap-2">
				<span className="truncate text-sm font-medium">{senderName}</span>
				<div className="ml-auto flex shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
					{turns > 1 && (
						<span
							className="inline-flex items-center gap-0.5"
							title={`${turns} messages`}
						>
							<MessageCircleIcon className="size-3" />
							{turns}
						</span>
					)}
					{hasAttachments && (
						<PaperclipIcon className="size-3" aria-label="Has attachments" />
					)}
					<span>{formatDateShort(last.receivedAt)}</span>
				</div>
			</div>
			<div className="truncate text-sm text-foreground">{thread.subject}</div>
			<p className="line-clamp-2 text-xs text-muted-foreground">{preview}</p>
			{intentTags.length > 0 && (
				<div className="flex flex-wrap items-center gap-1 pt-0.5">
					{intentTags.map((tag) => (
						<TagChip key={`${tag.axis}-${tag.label}`} tag={tag} />
					))}
				</div>
			)}
		</Button>
	);
}

// Thread detail (email-stack)

function ThreadDetail({
	thread,
	selectedMessageId,
	onSelectMessage,
}: {
	thread: FakeThread;
	selectedMessageId: string | null;
	onSelectMessage: (id: string) => void;
}) {
	return (
		<div className="flex h-full min-h-0 flex-col">
			<div className="flex shrink-0 items-center border-b px-2">
				<span className="px-3 py-2 text-sm font-semibold">Thread</span>
			</div>
			<ScrollArea className="h-full min-h-0 flex-1">
				<div className="flex flex-col">
					{thread.messages.map((msg, idx) => (
						<MessageBlock
							key={msg.id}
							message={msg}
							subject={idx === 0 ? thread.subject : undefined}
							selected={msg.id === selectedMessageId}
							onSelect={() => onSelectMessage(msg.id)}
							isLast={idx === thread.messages.length - 1}
						/>
					))}
				</div>
			</ScrollArea>
		</div>
	);
}

function MessageBlock({
	message,
	subject,
	selected,
	onSelect,
	isLast,
}: {
	message: FakeMessage;
	subject?: string;
	selected: boolean;
	onSelect: () => void;
	isLast: boolean;
}) {
	const outbound = message.direction === "outbound";
	return (
		<Button
			type="button"
			variant="ghost"
			onClick={onSelect}
			className={cn(
				"flex h-auto w-full flex-col items-stretch justify-start whitespace-normal rounded-none border-l-[3px] border-l-transparent p-0 text-left font-normal transition hover:bg-muted/20",
				!isLast && "border-b",
				outbound && !selected && "bg-muted/10",
				selected &&
					"border-l-primary bg-primary/[0.04] hover:bg-primary/[0.04]",
			)}
		>
			{subject && (
				<div className="px-5 pt-4 pb-1 text-base font-semibold text-foreground">
					{subject}
				</div>
			)}
			<div className="flex items-start gap-3 px-5 pt-3 pb-1">
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-baseline gap-x-2">
						<span className="truncate text-sm font-semibold">
							{message.from.name}
						</span>
						<span className="truncate text-xs text-muted-foreground">
							‹{message.from.email}›
						</span>
						{outbound && (
							<span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
								Sent
							</span>
						)}
					</div>
					<div className="mt-0.5 text-[11px] text-muted-foreground">
						<span className="font-medium text-foreground">To</span>{" "}
						{message.to.map((r) => r.name || r.email).join(", ")}
						{message.cc && message.cc.length > 0 && (
							<>
								<span className="mx-1.5">·</span>
								<span className="font-medium text-foreground">Cc</span>{" "}
								{message.cc.map((r) => r.name || r.email).join(", ")}
							</>
						)}
					</div>
				</div>
				<div className="flex shrink-0 items-center gap-2 pt-0.5 text-[11px] text-muted-foreground">
					{message.hasAttachments && <PaperclipIcon className="size-3" />}
					<span>{formatDateLong(message.receivedAt)}</span>
				</div>
			</div>
			<div className="whitespace-pre-wrap px-5 pb-4 pt-2 text-sm text-foreground/95">
				{message.bodyText}
			</div>
		</Button>
	);
}

// Workspace pane (Draft | Matter tabs)

function WorkspacePane({
	thread,
	matter,
	selectedMessageId,
}: {
	thread: FakeThread | null;
	matter: FakeMatter | null;
	selectedMessageId: string | null;
}) {
	if (!thread) {
		return (
			<div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
				No thread selected.
			</div>
		);
	}

	const selectedMessage =
		thread.messages.find((m) => m.id === selectedMessageId) ?? null;

	return (
		<Tabs defaultValue="draft" className="flex h-full min-h-0 flex-col gap-0">
			<div className="shrink-0 border-b px-3 py-2">
				<TabsList variant="line" className="w-full">
					<TabsTrigger value="draft">Draft</TabsTrigger>
					<TabsTrigger value="matter">Matter</TabsTrigger>
				</TabsList>
			</div>
			<TabsContent
				value="draft"
				className="flex min-h-0 flex-1 flex-col overflow-hidden"
			>
				<DraftPane
					key={thread.id}
					thread={thread}
					selectedMessage={selectedMessage}
				/>
			</TabsContent>
			<TabsContent
				value="matter"
				className="flex min-h-0 flex-1 flex-col overflow-hidden"
			>
				<MatterPanel thread={thread} matter={matter} />
			</TabsContent>
		</Tabs>
	);
}

// Draft pane

function DraftPane({
	thread,
	selectedMessage,
}: {
	thread: FakeThread;
	selectedMessage: FakeMessage | null;
}) {
	const [generating, setGenerating] = useState(false);
	const [localDraft, setLocalDraft] = useState<FakeDraft | null>(
		thread.fakeDraft ?? null,
	);

	const draftTarget =
		selectedMessage?.direction === "inbound" ? selectedMessage : null;

	if (!draftTarget) {
		return (
			<div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
				<SparklesIcon className="size-6 opacity-40" />
				<p className="font-medium text-foreground">Select an inbound message</p>
				<p className="text-xs">
					Click a received message in the thread to target it for a draft.
					Outbound messages can't be replied to.
				</p>
			</div>
		);
	}

	async function handleGenerate() {
		const target = draftTarget;
		if (!target) return;
		setGenerating(true);
		await new Promise((r) => setTimeout(r, 900));
		setGenerating(false);
		setLocalDraft({
			targetMessageId: target.id,
			model: "claude-sonnet-5",
			promptVersion: "drafter-v0.3 · practice-guide-v0",
			generatedAt: new Date().toISOString(),
			toolCalls: [
				{
					id: `tc-${Date.now()}-1`,
					toolName: "lookupMatter",
					title: "Looking up matter",
					input: {
						senderEmail: target.from.email,
					},
					output: thread.matterMatch
						? { matterId: thread.matterMatch.matterId, match: true }
						: { match: false },
				},
			],
			bodyText:
				"Hi,\n\n(This is a placeholder draft generated on the fly for this demo thread. Wire up real generation to replace this.)\n\nBest,\nPriya",
		});
	}

	return (
		<div className="flex h-full min-h-0 flex-col">
			<div className="shrink-0 border-b px-5 py-2 text-xs">
				<span className="text-muted-foreground">Replying to </span>
				<span className="font-medium text-foreground">
					{draftTarget.from.name}
				</span>
				<span className="text-muted-foreground">
					{" "}
					· {formatDateLong(draftTarget.receivedAt)}
				</span>
			</div>
			{localDraft ? (
				<DraftContent
					draft={localDraft}
					onRegenerate={handleGenerate}
					regenerating={generating}
				/>
			) : (
				<div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
					<SparklesIcon className="size-6 text-muted-foreground" />
					<div>
						<p className="text-sm font-medium">No draft yet</p>
						<p className="text-xs text-muted-foreground">
							Generate a draft reply for this message.
						</p>
					</div>
					<Button type="button" onClick={handleGenerate} disabled={generating}>
						<SparklesIcon />
						{generating ? "Drafting…" : "Generate draft"}
					</Button>
				</div>
			)}
		</div>
	);
}

function DraftContent({
	draft,
	onRegenerate,
	regenerating,
}: {
	draft: FakeDraft;
	onRegenerate: () => void;
	regenerating: boolean;
}) {
	return (
		<>
			<ScrollArea className="h-full min-h-0 flex-1">
				<div className="flex flex-col">
					{draft.toolCalls.length > 0 && (
						<div className="flex flex-col gap-0.5 px-5 py-3">
							{draft.toolCalls.map((call) => (
								<Tool key={call.id} defaultOpen={false}>
									<ToolHeader title={call.title} state="done" />
									<ToolContent>
										<ToolInput input={call.input} />
										<ToolOutput output={call.output} />
									</ToolContent>
								</Tool>
							))}
						</div>
					)}

					<div className="whitespace-pre-wrap px-5 py-4 text-sm leading-relaxed text-foreground">
						{draft.bodyText}
					</div>
				</div>
			</ScrollArea>

			<div className="flex shrink-0 items-center justify-end border-t bg-muted/20 px-5 py-2.5">
				<Button
					type="button"
					variant="outline"
					size="sm"
					onClick={onRegenerate}
					disabled={regenerating}
				>
					<RefreshCwIcon className={cn(regenerating && "animate-spin")} />
					{regenerating ? "Regenerating…" : "Regenerate"}
				</Button>
			</div>
		</>
	);
}

// Matter panel

function MatterPanel({
	thread,
	matter,
}: {
	thread: FakeThread | null;
	matter: FakeMatter | null;
}) {
	if (!thread) {
		return (
			<div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
				No thread selected.
			</div>
		);
	}
	if (!thread.matterMatch || !matter) {
		return (
			<div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center text-sm text-muted-foreground">
				<FolderIcon className="size-8 opacity-40" />
				<div>
					<p className="font-medium text-foreground">No matter linked</p>
					<p className="text-xs">
						The sender doesn't appear in the firm's Clio contacts.
					</p>
				</div>
				<Button type="button" variant="outline" size="sm">
					Link to a matter manually
				</Button>
			</div>
		);
	}

	return (
		<div className="flex h-full min-h-0 flex-col">
			<div className="flex flex-col gap-1 border-b px-5 py-3">
				<h3 className="truncate text-base font-semibold">
					{matter.clientName}
				</h3>
				<div className="truncate text-xs text-muted-foreground">
					{matter.matterType} · {matter.stage}
				</div>
			</div>
			<ScrollArea className="h-full min-h-0 flex-1">
				<div className="flex flex-col gap-4 px-5 py-4">
					<Section title="Summary">
						<p className="whitespace-pre-wrap text-sm text-foreground/90">
							{matter.summary}
						</p>
					</Section>

					<Section title="Contacts">
						<ul className="flex flex-col gap-2">
							{matter.contacts.map((c) => (
								<li
									key={`${c.name}-${c.role}`}
									className="flex items-start gap-2 text-sm"
								>
									<UserIcon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
									<div className="min-w-0">
										<div className="font-medium">{c.name}</div>
										<div className="text-xs text-muted-foreground">
											{c.role}
											{c.email ? ` · ${c.email}` : ""}
											{c.phone ? ` · ${c.phone}` : ""}
										</div>
									</div>
								</li>
							))}
						</ul>
					</Section>

					<Section title={`Recent activity (${matter.timeline.length})`}>
						<ul className="flex flex-col gap-2">
							{matter.timeline
								.slice()
								.reverse()
								.map((e) => (
									<li
										key={`${e.date}-${e.label}`}
										className="flex items-start gap-2 text-sm"
									>
										<span className="mt-1 size-1.5 shrink-0 rounded-full bg-muted-foreground/50" />
										<div className="min-w-0 flex-1">
											<div className="flex items-center gap-2">
												<span className="text-xs text-muted-foreground">
													{e.date}
												</span>
												<span className="text-[9px] uppercase tracking-wide text-muted-foreground/70">
													{e.source}
												</span>
											</div>
											<div className="text-sm text-foreground">{e.label}</div>
											{e.detail && (
												<div className="text-xs text-muted-foreground">
													{e.detail}
												</div>
											)}
										</div>
									</li>
								))}
						</ul>
					</Section>

					{matter.openTasks.length > 0 && (
						<Section title={`Open tasks (${matter.openTasks.length})`}>
							<ul className="flex flex-col gap-1">
								{matter.openTasks.map((t) => (
									<li key={t.title} className="flex items-start gap-2 text-sm">
										<span className="mt-1 size-1.5 shrink-0 rounded-full border border-muted-foreground/50" />
										<div className="min-w-0 flex-1">
											<div>{t.title}</div>
											<div className="text-xs text-muted-foreground">
												{t.assignee}
												{t.dueDate ? ` · due ${t.dueDate}` : ""}
											</div>
										</div>
									</li>
								))}
							</ul>
						</Section>
					)}

					{matter.documents.length > 0 && (
						<Section title={`Documents (${matter.documents.length})`}>
							<ul className="flex flex-col gap-1.5">
								{matter.documents.map((d) => (
									<li
										key={d.path + d.name}
										className="flex items-start gap-2 text-sm"
									>
										<FileTextIcon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
										<div className="min-w-0 flex-1">
											<div className="truncate text-sm">{d.name}</div>
											<div className="truncate text-xs text-muted-foreground">
												{d.path} · {d.size} · {d.modifiedAt}
											</div>
										</div>
									</li>
								))}
							</ul>
						</Section>
					)}

					<Section title="Custom fields">
						<dl className="flex flex-col gap-1 text-sm">
							{matter.receiptNumber && (
								<div className="flex items-baseline justify-between gap-3">
									<dt className="text-xs text-muted-foreground">
										Receipt number
									</dt>
									<dd className="truncate font-mono text-xs">
										{matter.receiptNumber}
									</dd>
								</div>
							)}
							<div className="flex items-baseline justify-between gap-3">
								<dt className="text-xs text-muted-foreground">Opened</dt>
								<dd className="truncate font-mono text-xs">
									{matter.openedAt}
								</dd>
							</div>
							{matter.customFields.map((f) => (
								<div
									key={f.label}
									className="flex items-baseline justify-between gap-3"
								>
									<dt className="text-xs text-muted-foreground">{f.label}</dt>
									<dd className="truncate font-mono text-xs">{f.value}</dd>
								</div>
							))}
						</dl>
					</Section>

					{matter.notes.length > 0 && (
						<Section title={`Matter notes (${matter.notes.length})`}>
							<ul className="flex flex-col gap-2">
								{matter.notes.map((n) => (
									<li
										key={`${n.date}-${n.author}`}
										className="rounded-md border bg-background p-2.5"
									>
										<div className="flex items-center justify-between text-[11px] text-muted-foreground">
											<span className="font-medium text-foreground">
												{n.author}
											</span>
											<span>{n.date}</span>
										</div>
										<p className="mt-1 whitespace-pre-wrap text-xs text-foreground/90">
											{n.body}
										</p>
									</li>
								))}
							</ul>
						</Section>
					)}
				</div>
			</ScrollArea>
		</div>
	);
}

// Tag chip

const TAG_AXIS_STYLES: Record<FakeTag["axis"], string> = {
	intent: "bg-chart-1/10 text-chart-1 ring-chart-1/20",
	stage: "bg-chart-2/10 text-chart-2 ring-chart-2/20",
	who: "bg-chart-3/10 text-chart-3 ring-chart-3/20",
	matter_type: "bg-chart-4/10 text-chart-4 ring-chart-4/20",
};

function TagChip({ tag }: { tag: FakeTag }) {
	return (
		<span
			title={tag.axis}
			className={cn(
				"inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset",
				TAG_AXIS_STYLES[tag.axis],
			)}
		>
			{tag.label}
		</span>
	);
}

function Section({
	title,
	children,
}: {
	title: string;
	children: React.ReactNode;
}) {
	return (
		<section className="flex flex-col gap-2">
			<h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
				{title}
			</h4>
			<div>{children}</div>
			<Separator className="mt-2" />
		</section>
	);
}
